import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  // Admin state - persisted in sessionStorage
  const [isAdmin, setIsAdmin] = useState(() => {
    // Check sessionStorage on initial load
    return sessionStorage.getItem('popperverse_admin') === 'true';
  });
  
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [adminCodeInput, setAdminCodeInput] = useState('');

  // Persist admin state to sessionStorage
  useEffect(() => {
    if (isAdmin) {
      sessionStorage.setItem('popperverse_admin', 'true');
    } else {
      sessionStorage.removeItem('popperverse_admin');
    }
  }, [isAdmin]);

  // Verify admin code
  const verifyAdminCode = useCallback((code) => {
    const adminCode = process.env.REACT_APP_ADMIN_CODE || 'popperverse2024';
    if (code === adminCode) {
      setIsAdmin(true);
      setShowAdminModal(false);
      setAdminCodeInput('');
      return true;
    }
    return false;
  }, []);

  // Logout admin
  const logoutAdmin = useCallback(() => {
    setIsAdmin(false);
    setAdminCodeInput('');
    setShowAdminModal(false);
  }, []);

  // Toggle admin modal
  const toggleAdminModal = useCallback(() => {
    setShowAdminModal(prev => !prev);
  }, []);

  const value = {
    isAdmin,
    setIsAdmin,
    showAdminModal,
    setShowAdminModal,
    adminCodeInput,
    setAdminCodeInput,
    verifyAdminCode,
    logoutAdmin,
    toggleAdminModal,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
